import { useEffect } from "react";
import { useRefresh } from "react-admin";
import { hexToRgb } from "@my-scoot/exly-react-component-lib";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { baseApiClient } from "data/modules/api/api.client";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { checkIsObjectEmpty } from "features/Common/modules/DataTypes/utils/object";
import { is_empty } from "features/Common/utils/common.utils";
import { CHANGE_STATES } from "features/Common/modules/Form/constants/Form.constants";
import { rgbToHex } from "ui/pages/TemplatePreview/utils";
import { isRequestSuccessful } from "utils/Utils";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { logError } from "utils/error";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import {
  CHANNEL_API_KEYS,
  CUSTOM_LINK_API_KEYS,
  CMTModalColorScheme as colorScheme,
  initialMembershipTierState,
  MEMBERSHIP_SECTION_KEY,
  MEMBERSHIP_TIER_DETAILS_KEYS,
  membership_custom_link_error_messages,
  membership_custom_link_log_errors,
  membership_kyc_display_strings,
  rgbRegex,
} from "../../constants/MembershipsSection.data";
import { PER_TIER_KYC_FIELD_KEYS } from "features/ManageOfferings/modules/PerTierKyc/PerTierKyc.constants";
import { useMembershipStore } from "../../store/useMembershipStore";
import {
  MEMBERSHIP_API_PATHNAME,
  MEMBERSHIP_APIS,
} from "../../constants/Membership.apis";
import { useChannelDropdown } from "../../modules/CreateMembershipTierModal/modules/ChannelDropdown/utils/hooks/useChannelDropdown";
import {
  extractEditedCustomLinks,
  syncCustomLinksForMembership,
} from "../customLink.utils";
import { syncChannelsForMembership } from "../membershipChannel.utils";
import {
  getChannelMappingChangeLabels,
  getLinkMappingChangeLabels,
} from "../membershipMappingChange.utils";
import { useCommunityCustomLinkOptions } from "./useCommunityCustomLinkOptions";
import { navigateTo } from "utils/urlUtils";
import { MANAGE_OFFERINGS_ROUTE_KEYS } from "features/ManageOfferings/constants/ManageOfferings.urlPaths";

export const useMembership = ({
  canFetchMemberSettings = false,
  isInitialiseMembershipDetails = false,
} = {}) => {
  const refresh = useRefresh();
  const { notify } = useNotifications();
  const { getChannelList, extractEditedChannels, channelOptionList } =
    useChannelDropdown();
  const membershipTierDetails = useMembershipStore(
    (state) => state.membershipTierDetails
  );
  const listingId = useMembershipStore((state) => state.listingId);
  const setMembershipList = useMembershipStore(
    (state) => state.setMembershipList
  );
  const initialiseMemberTierDetails = useMembershipStore(
    (state) => state.initialiseMemberTierDetails
  );
  const setMembershipTierDetails = useMembershipStore(
    (state) => state.setMembershipTierDetails
  );
  const selectedRecord = useMembershipStore((state) => state.selectedRecord);
  const setSelectedRecord = useMembershipStore(
    (state) => state.setSelectedRecord
  );
  const setIsOpenCreateMembershipTierModal = useMembershipStore(
    (state) => state.setIsOpenCreateMembershipTierModal
  );
  const setIsOpenDeleteConfirmation = useMembershipStore(
    (state) => state.setIsOpenDeleteConfirmation
  );
  const setIsClicked = useMembershipStore((state) => state.setIsClicked);
  const initialMappedCustomLinks = useMembershipStore(
    (state) => state.initialMappedCustomLinks
  );
  const setInitialMappedCustomLinks = useMembershipStore(
    (state) => state.setInitialMappedCustomLinks
  );
  const setIsMembershipListLoading = useMembershipStore(
    (state) => state.setIsMembershipListLoading
  );
  const isOpenCreateMembershipTierModal = useMembershipStore(
    (state) => state.isOpenCreateMembershipTierModal
  );

  const { customLinkOptionList } = useCommunityCustomLinkOptions({
    enabled: isOpenCreateMembershipTierModal,
  });

  const { mutate: createMembership } = useCustomMutation({
    url: MEMBERSHIP_APIS.createMembership,
    onSuccess: () => {},
    onError: () => {},
  });

  const { mutate: updateMembership } = useCustomMutation({
    url: MEMBERSHIP_APIS.editMembership,
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: membershipSettings, refetch: refetchMemebershipSettings } =
    useCustomQuery({
      queryKey: [MEMBERSHIP_APIS.membershipSettings, listingId],
      url: MEMBERSHIP_API_PATHNAME.membershipSettings(listingId),
      enabled: !!canFetchMemberSettings && !!listingId,
    });

  const getChannelNameById = () => {
    const channelNameById = {};

    (channelOptionList || []).forEach((channel) => {
      const channelUuid = channel?.[CHANNEL_API_KEYS.CHANNEL_ID];
      if (channelUuid) {
        channelNameById[channelUuid] =
          channel?.[CHANNEL_API_KEYS.CHANNEL_NAME] || channel?.title;
      }
    });

    (getChannelList() || []).forEach((channel) => {
      const channelUuid = channel?.[CHANNEL_API_KEYS.CHANNEL_ID];
      if (channelUuid && channel?.title) {
        channelNameById[channelUuid] = channel.title;
      }
    });

    return channelNameById;
  };

  const getLinkNameById = () => {
    const linkNameById = {};

    (customLinkOptionList || []).forEach((link) => {
      const linkUuid = link?.[CUSTOM_LINK_API_KEYS.LINK_UUID];
      if (linkUuid && link?.title) {
        linkNameById[linkUuid] = link.title;
      }
    });

    const allLinks = [
      ...(initialMappedCustomLinks || []),
      ...(membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.CUSTOM_LINKS] ||
        []),
    ];

    allLinks.forEach((link) => {
      const linkUuid = link?.[CUSTOM_LINK_API_KEYS.LINK_UUID];
      if (linkUuid && link?.title) {
        linkNameById[linkUuid] = link.title;
      }
    });

    return linkNameById;
  };

  const getEditedMappingDiffs = () => {
    const editedChannels = extractEditedChannels();
    const editedLinks = extractEditedCustomLinks({
      existingLinks: initialMappedCustomLinks,
      updatedLinks:
        membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.CUSTOM_LINKS] ||
        [],
    });

    return { editedChannels, editedLinks };
  };

  const hasEditMappingChanges = () => {
    const { editedChannels, editedLinks } = getEditedMappingDiffs();
    return (
      !checkIsArrayEmpty(editedChannels) || !checkIsArrayEmpty(editedLinks)
    );
  };

  const getEditMappingChanges = () => {
    const { editedChannels, editedLinks } = getEditedMappingDiffs();

    return [
      ...getChannelMappingChangeLabels({
        editedChannels,
        channelNameById: getChannelNameById(),
      }),
      ...getLinkMappingChangeLabels({
        editedLinks,
        linkNameById: getLinkNameById(),
      }),
    ];
  };

  useEffect(() => {
    if (!selectedRecord) {
      if (isInitialiseMembershipDetails) {
        initialiseMemberTierDetails({ ...initialMembershipTierState });
      }
    } else {
      const recordThemeColors =
        selectedRecord?.metadata?.[MEMBERSHIP_TIER_DETAILS_KEYS.COLOR];
      const themeColors = !checkIsObjectEmpty(recordThemeColors)
        ? { ...themeColorToRgb({ themeColors: recordThemeColors }) }
        : colorScheme.colors;

      let initialMembershipData = {
        title: selectedRecord?.[MEMBERSHIP_TIER_DETAILS_KEYS.TITLE],
        description: selectedRecord?.[MEMBERSHIP_TIER_DETAILS_KEYS.DESCRIPTION],
        price_per_head: selectedRecord?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE],
        price_international:
          selectedRecord?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE_INTERNATIONAL],
        is_international_enabled:
          selectedRecord?.[
            MEMBERSHIP_TIER_DETAILS_KEYS.IS_INTERNATIONAL_ENABLED
          ],
        icon: "",
        is_popular:
          selectedRecord?.metadata?.[MEMBERSHIP_TIER_DETAILS_KEYS.IS_POPULAR],
        perks: selectedRecord?.metadata?.[MEMBERSHIP_TIER_DETAILS_KEYS.PERKS],
        color: themeColors,
        channels: selectedRecord?.[MEMBERSHIP_TIER_DETAILS_KEYS.CHANNELS],
      };
      if (!selectedRecord?.metadata || is_empty(selectedRecord?.metadata)) {
        initialMembershipData.is_popular = false;
        initialMembershipData.perks = [];
      }
      initialiseMemberTierDetails({ ...initialMembershipData });
    }
  }, [selectedRecord]);

  const getMembershipList = async ({ listingUuid }) => {
    if (listingUuid && !is_empty(listingUuid)) {
      const { isMembershipListLoading } = useMembershipStore.getState();
      if (isMembershipListLoading) return;

      setIsMembershipListLoading(true);
      try {
        const response = await baseApiClient.get(
          MEMBERSHIP_API_PATHNAME.getMembershipsList(listingUuid)
        );
        if (isRequestSuccessful(response?.status)) {
          setMembershipList({
            list: response?.data?.data?.community_membership_list,
            listSize: response?.data?.data?.community_membership_list.length,
          });
        } else {
          notify(
            response?.data?.message || SOMETHING_WENT_WRONG,
            notification_color_keys.error
          );
        }
      } catch (error) {
        notify(
          error?.response?.data?.message || SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
        logError({
          error,
          customErrorMessage: "Error while get membership list",
          when: "calling getMembershipList",
          occuredAt:
            "src/features/ManageOfferings/modules/MembershipsSection/utils/hooks/useMembership.js",
          severity: "blocker",
        });
      } finally {
        setIsMembershipListLoading(false);
      }
    }
  };

  const themeColorToHex = ({ themeColors }) => {
    if (!checkIsObjectEmpty(themeColors)) {
      return {
        [colorScheme.data.primaryCard]: rgbToHex(
          themeColors?.[colorScheme.data.primaryCard]
        ),
        [colorScheme.data.detailCard]: rgbToHex(
          themeColors?.[colorScheme.data.detailCard]
        ),
        [colorScheme.data.textCard]: rgbToHex(
          themeColors?.[colorScheme.data.textCard]
        ),
        [colorScheme.data.buttonCard]: rgbToHex(
          themeColors?.[colorScheme.data.buttonCard]
        ),
      };
    } else {
      return {};
    }
  };

  const themeColorToRgb = ({ themeColors }) => {
    if (!checkIsObjectEmpty(themeColors)) {
      return {
        [colorScheme.data.primaryCard]: hexToRgb(
          themeColors?.[colorScheme.data.primaryCard]
        ).match(rgbRegex)[1],
        [colorScheme.data.detailCard]: hexToRgb(
          themeColors?.[colorScheme.data.detailCard]
        ).match(rgbRegex)[1],
        [colorScheme.data.textCard]: hexToRgb(
          themeColors?.[colorScheme.data.textCard]
        ).match(rgbRegex)[1],
        [colorScheme.data.buttonCard]: hexToRgb(
          themeColors?.[colorScheme.data.buttonCard]
        ).match(rgbRegex)[1],
      };
    } else {
      return {};
    }
  };

  const handleThemeColorChange = ({ themeColorKey, value }) => {
    const updatedThemeColor = {
      ...membershipTierDetails[MEMBERSHIP_TIER_DETAILS_KEYS.COLOR],
    };
    if (themeColorKey) {
      updatedThemeColor[themeColorKey] = value;
    }
    setMembershipTierDetails(
      MEMBERSHIP_TIER_DETAILS_KEYS.COLOR,
      updatedThemeColor
    );
  };

  const validateMembershipDetails = () => {
    let isValid = true;
    if (
      !membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.TITLE] ||
      is_empty(membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.TITLE])
    ) {
      notify("Please enter membership title", notification_color_keys.error);
      isValid = false;
    }
    if (
      !membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.DESCRIPTION] ||
      is_empty(
        membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.DESCRIPTION]
      )
    ) {
      notify(
        "Please enter membership description",
        notification_color_keys.error
      );
      isValid = false;
    }
    if (
      !membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE] ||
      is_empty(membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE])
    ) {
      notify("Please enter membership price", notification_color_keys.error);
      isValid = false;
    }
    if (
      membershipTierDetails?.[
        MEMBERSHIP_TIER_DETAILS_KEYS.IS_INTERNATIONAL_ENABLED
      ] &&
      (!membershipTierDetails?.[
        MEMBERSHIP_TIER_DETAILS_KEYS.PRICE_INTERNATIONAL
      ] ||
        is_empty(
          membershipTierDetails?.[
            MEMBERSHIP_TIER_DETAILS_KEYS.IS_INTERNATIONAL_ENABLED
          ]
        ))
    ) {
      notify(
        "Please enter membership international price",
        notification_color_keys.error
      );
      isValid = false;
    }
    return isValid;
  };

  const getUpdateMembershipPayload = () => {
    let payload = {};
    if (
      selectedRecord?.[MEMBERSHIP_TIER_DETAILS_KEYS.TITLE] !==
      membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.TITLE]
    ) {
      payload = {
        ...payload,
        [MEMBERSHIP_TIER_DETAILS_KEYS.TITLE]:
          membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.TITLE],
      };
    }
    if (
      selectedRecord?.[MEMBERSHIP_TIER_DETAILS_KEYS.DESCRIPTION] !==
      membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.DESCRIPTION]
    ) {
      payload = {
        ...payload,
        [MEMBERSHIP_TIER_DETAILS_KEYS.DESCRIPTION]:
          membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.DESCRIPTION],
      };
    }
    if (
      selectedRecord?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE] !==
      membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE]
    ) {
      payload = {
        ...payload,
        [MEMBERSHIP_TIER_DETAILS_KEYS.PRICE]:
          membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE],
      };
    }
    if (
      selectedRecord?.[
        MEMBERSHIP_TIER_DETAILS_KEYS.IS_INTERNATIONAL_ENABLED
      ] !==
      membershipTierDetails?.[
        MEMBERSHIP_TIER_DETAILS_KEYS.IS_INTERNATIONAL_ENABLED
      ]
    ) {
      payload = {
        ...payload,
        [MEMBERSHIP_TIER_DETAILS_KEYS.IS_INTERNATIONAL_ENABLED]:
          membershipTierDetails?.[
            MEMBERSHIP_TIER_DETAILS_KEYS.IS_INTERNATIONAL_ENABLED
          ],
      };
    }
    if (
      selectedRecord?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE_INTERNATIONAL] !==
      membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE_INTERNATIONAL]
    ) {
      payload = {
        ...payload,
        [MEMBERSHIP_TIER_DETAILS_KEYS.PRICE_INTERNATIONAL]:
          membershipTierDetails?.[
            MEMBERSHIP_TIER_DETAILS_KEYS.PRICE_INTERNATIONAL
          ],
      };
    }
    payload = {
      ...payload,
      metadata: {
        // Preserve existing metadata (e.g. kyc_override): the backend
        // replaces metadata wholesale, so rebuilding it from form fields
        // alone would silently reset a custom KYC verification to default.
        ...(selectedRecord?.metadata || {}),
        [MEMBERSHIP_TIER_DETAILS_KEYS.IS_POPULAR]:
          membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.IS_POPULAR],
        [MEMBERSHIP_TIER_DETAILS_KEYS.PERKS]: [
          ...membershipTierDetails[MEMBERSHIP_TIER_DETAILS_KEYS.PERKS],
        ],
        [MEMBERSHIP_TIER_DETAILS_KEYS.COLOR]: {
          ...themeColorToHex({
            themeColors:
              membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.COLOR],
          }),
        },
      },
    };
    return payload;
  };

  const createMembershipRequest = () => {
    setIsClicked(true);
    const payload = {
      community_listing_uuid: listingId,
      title: membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.TITLE],
      description:
        membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.DESCRIPTION],
      price_per_head: parseInt(
        membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.PRICE]
      ),
      price_international:
        membershipTierDetails?.[
          MEMBERSHIP_TIER_DETAILS_KEYS.PRICE_INTERNATIONAL
        ] ?? 0,
      is_international_enabled:
        membershipTierDetails?.[
          MEMBERSHIP_TIER_DETAILS_KEYS.IS_INTERNATIONAL_ENABLED
        ],
      icon: "",
      metadata: {
        is_popular:
          membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.IS_POPULAR],
        perks: [...membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.PERKS]],
        color: {
          ...themeColorToHex({
            themeColors:
              membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.COLOR],
          }),
        },
      },
      channels: [
        ...membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.CHANNELS],
      ],
    };
    if (membershipTierDetails && !checkIsObjectEmpty(membershipTierDetails)) {
      if (!validateMembershipDetails()) {
        setIsClicked(false);
        return;
      }
      const handleSuccess = async (responseData) => {
        const membershipUuid = responseData?.uuid || responseData?.data?.uuid;
        const selectedLinks =
          membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.CUSTOM_LINKS] ||
          [];

        let customLinkMappingFailed = false;

        try {
          if (membershipUuid && !checkIsArrayEmpty(selectedLinks)) {
            await syncCustomLinksForMembership({
              membershipUuid,
              editedLinks: selectedLinks.map((link) => ({
                ...link,
                change_type: CHANGE_STATES.added,
              })),
            });
          }
        } catch (error) {
          customLinkMappingFailed = true;
          logError({
            error,
            customErrorMessage:
              membership_custom_link_log_errors.mapping_failed_on_create,
            when: "calling createMembershipRequest handleSuccess",
            occuredAt:
              "src/features/ManageOfferings/modules/MembershipsSection/utils/hooks/useMembership.js",
            severity: "blocker",
          });
        }

        setIsOpenCreateMembershipTierModal(false);
        setSelectedRecord(null);
        setInitialMappedCustomLinks([]);
        initialiseMemberTierDetails({ ...initialMembershipTierState });
        setIsClicked(false);
        refresh();
        notify(
          "Membership created successfully.",
          notification_color_keys.success
        );
        if (customLinkMappingFailed) {
          notify(
            membership_custom_link_error_messages.mapping_failed_on_create,
            notification_color_keys.error
          );
        }
      };

      const handleError = (error) => {
        setIsClicked(false);
        notify(
          error?.response?.data?.message || SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
        logError({
          error,
          customErrorMessage: "Error while create membership",
          when: "calling createMembershipRequest",
          occuredAt:
            "src/features/ManageOfferings/modules/MembershipsSection/utils/useMembership.js",
          severity: "blocker",
        });
      };

      createMembership(
        { ...payload },
        { onSuccess: handleSuccess, onError: handleError }
      );
    } else {
      setIsClicked(false);
      notify("Invalid Data", notification_color_keys.error);
    }
  };

  const performUpdateMembership = ({
    selectedRecord: record,
    applyToExistingCustomers = false,
  }) => {
    setIsClicked(true);
    const { editedChannels, editedLinks } = getEditedMappingDiffs();
    const payload = getUpdateMembershipPayload();

    if (membershipTierDetails && !checkIsObjectEmpty(membershipTierDetails)) {
      if (!validateMembershipDetails({ membershipTierDetails })) {
        setIsClicked(false);
        return Promise.reject();
      }

      return new Promise((resolve, reject) => {
        const handleSuccess = async () => {
          let channelMappingFailed = false;
          let customLinkMappingFailed = false;

          try {
            if (!checkIsArrayEmpty(editedChannels)) {
              await syncChannelsForMembership({
                communityListingUuid: listingId,
                membershipUuid: record?.uuid,
                editedChannels,
                applyToExistingCustomers,
              });
            }
          } catch (error) {
            channelMappingFailed = true;
            logError({
              error,
              customErrorMessage: "Error while syncing membership channels",
              when: "calling performUpdateMembership syncChannelsForMembership",
              occuredAt:
                "src/features/ManageOfferings/modules/MembershipsSection/utils/hooks/useMembership.js",
              severity: "blocker",
            });
          }

          try {
            if (!checkIsArrayEmpty(editedLinks)) {
              await syncCustomLinksForMembership({
                communityListingUuid: listingId,
                membershipUuid: record?.uuid,
                editedLinks,
                applyToExistingCustomers,
              });
            }
          } catch (error) {
            customLinkMappingFailed = true;
            logError({
              error,
              customErrorMessage:
                membership_custom_link_log_errors.mapping_failed_on_update,
              when: "calling performUpdateMembership syncCustomLinksForMembership",
              occuredAt:
                "src/features/ManageOfferings/modules/MembershipsSection/utils/hooks/useMembership.js",
              severity: "blocker",
            });
          }

          setIsOpenCreateMembershipTierModal(false);
          setSelectedRecord(null);
          setInitialMappedCustomLinks([]);
          initialiseMemberTierDetails({ ...initialMembershipTierState });
          setIsClicked(false);
          refresh();
          notify(
            "Membership updated successfully.",
            notification_color_keys.success
          );

          if (channelMappingFailed || customLinkMappingFailed) {
            notify(
              membership_custom_link_error_messages.mapping_failed_on_update,
              notification_color_keys.error
            );
          }

          resolve();
        };

        const handleError = (error) => {
          setIsClicked(false);
          notify(
            error?.response?.data?.message || SOMETHING_WENT_WRONG,
            notification_color_keys.error
          );
          logError({
            error,
            customErrorMessage: "Error while updating membership",
            when: "calling performUpdateMembership",
            occuredAt:
              "src/features/ManageOfferings/modules/MembershipsSection/utils/hooks/useMembership.js",
            severity: "blocker",
          });
          reject(error);
        };

        updateMembership(
          {
            community_listing_uuid: listingId,
            membership_uuid: record?.uuid,
            updated_values: { ...payload },
          },
          { onSuccess: handleSuccess, onError: handleError }
        );
      });
    }

    setIsClicked(false);
    notify("Invalid Data", notification_color_keys.error);
    return Promise.reject();
  };

  const deleteMembershipRequest = async () => {
    if (selectedRecord && selectedRecord?.uuid && listingId) {
      try {
        const channelList =
          getChannelList({ status: CHANGE_STATES.deleted }) || [];
        const payload = {
          community_listing_uuid: listingId,
          membership_uuid: selectedRecord?.uuid,
          updated_values: {
            title: selectedRecord?.title,
            description: selectedRecord?.description,
            channels: [...channelList],
          },
        };
        const response = await baseApiClient.post(
          MEMBERSHIP_API_PATHNAME.deleteMemebership({
            listingUuid: listingId,
            membershipUuid: selectedRecord?.uuid,
          }),
          payload
        );
        if (isRequestSuccessful(response.status)) {
          setIsOpenDeleteConfirmation(false);
          refresh();
          setSelectedRecord(null);
          notify(
            "Membership deleted successfully.",
            notification_color_keys.success
          );
        } else {
          notify(
            response?.data?.data?.message || SOMETHING_WENT_WRONG,
            notification_color_keys.error
          );
        }
      } catch (error) {
        notify(SOMETHING_WENT_WRONG, notification_color_keys.error);
        logError({
          error,
          customErrorMessage: "Error while updating membership",
          when: "calling deleteMembershipRequest",
          occuredAt:
            "src/features/ManageOfferings/modules/MembershipsSection/utils/useMembership.js",
          severity: "blocker",
        });
      }
    } else {
      notify("Invalid Data", notification_color_keys.error);
    }
  };

  const getColorVal = (colorKeyProp) =>
    membershipTierDetails?.[MEMBERSHIP_TIER_DETAILS_KEYS.COLOR]?.[
      colorKeyProp
    ] || "255, 255, 0";

  const handleCreateMembershipModaleClose = () => {
    setIsOpenCreateMembershipTierModal(false);
    setSelectedRecord(null);
    setInitialMappedCustomLinks([]);
    initialiseMemberTierDetails({ ...initialMembershipTierState });
  };

  const handleInternationalToggle = ({ checked }) => {
    setMembershipTierDetails(
      MEMBERSHIP_TIER_DETAILS_KEYS.IS_INTERNATIONAL_ENABLED,
      checked
    );
    setMembershipTierDetails(
      MEMBERSHIP_TIER_DETAILS_KEYS.PRICE_INTERNATIONAL,
      null
    );
  };

  const handleCreateClick = ({ listingUuid }) => {
    navigateTo(MANAGE_OFFERINGS_ROUTE_KEYS.MEMBERSHIPS, {
      args: {
        listing_uuid: listingUuid,
        section_key: MEMBERSHIP_SECTION_KEY,
      },
    });
    setIsOpenCreateMembershipTierModal(true);
  };

  const updateMembershipKycOverride = ({ record, override }) => {
    if (!record?.uuid || !listingId) {
      notify(
        membership_kyc_display_strings.invalid_data,
        notification_color_keys.error
      );
      return Promise.reject();
    }
    return new Promise((resolve, reject) => {
      const handleSuccess = (responseData) => {
        notify(
          membership_kyc_display_strings.update_success,
          notification_color_keys.success
        );
        resolve(responseData);
      };
      const handleError = (error) => {
        notify(
          error?.response?.data?.message || SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
        logError({
          error,
          customErrorMessage: "Error while updating membership KYC override",
          when: "calling updateMembershipKycOverride",
          occuredAt:
            "src/features/ManageOfferings/modules/MembershipsSection/utils/hooks/useMembership.js",
          severity: "blocker",
        });
        reject(error);
      };
      updateMembership(
        {
          community_listing_uuid: listingId,
          membership_uuid: record?.uuid,
          updated_values: {
            metadata: {
              ...(record?.metadata || {}),
              [PER_TIER_KYC_FIELD_KEYS.KYC_OVERRIDE]: override,
            },
          },
        },
        { onSuccess: handleSuccess, onError: handleError }
      );
    });
  };

  return {
    membershipSettings,
    refetchMemebershipSettings,
    getMembershipList,
    createMembershipRequest,
    performUpdateMembership,
    updateMembershipKycOverride,
    deleteMembershipRequest,
    hasEditMappingChanges,
    getEditMappingChanges,
    themeColorToHex,
    themeColorToRgb,
    handleThemeColorChange,
    getColorVal,
    handleCreateMembershipModaleClose,
    handleInternationalToggle,
    handleCreateClick,
  };
};
